import { useCallback, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from '@tanstack/react-router';
import { FiPlus } from 'react-icons/fi';
import { Badge } from '@/shared/ui/badge';
import { Button } from '@/shared/ui/button';
import { ErrorState } from '@/shared/feedback';
import { AppList } from '@/shared/app-list-layout';
import type { DataTableView } from '@/shared/app-list-layout';
import type { ListColumn } from '@/shared/app-list-layout/types';
import { formatDate } from '@/shared/format';
import {
  CapabilityBadges,
  CredentialSummaryLine,
  RotateCredentialDialog,
  providerStateTone,
} from '@/modules/ai/shared';
import { aiApi } from '@/modules/ai/services/ai-api';
import { providerActionHandlers } from './provider-actions';
import type { ProviderSummary, ProviderTestResult } from '@/api/schemas/ai';

/**
 * The platform's AI providers.
 *
 * ---------------------------------------------------------------------------
 * NO CREDENTIAL VALUE IS EVER SHOWN
 * ---------------------------------------------------------------------------
 * The credential column carries the STATUS only — configured or not, the key
 * version, the last rotation date. Rotating a credential opens a write-only
 * dialog; the value never comes back, because the API never sends it.
 *
 * The list is the shared `AppList` surface, so it carries the same tabs, search,
 * filters, pager and table/cards toggle as every other list in the console.
 */

/** State options surfaced in the column-bound filter dropdown. */
const STATE_OPTIONS = [
  { value: 'active', label: 'Active' },
  { value: 'disabled', label: 'Disabled' },
  { value: 'retired', label: 'Retired' },
];

/** Saved views rendered as tabs above the provider list. */
function buildProviderViews(counts: Partial<Record<string, number>>): DataTableView[] {
  return [
    { id: 'all', label: 'All providers', count: counts.all },
    { id: 'active', label: 'Active', filters: { state: 'active' }, count: counts.active },
    { id: 'disabled', label: 'Disabled', filters: { state: 'disabled' }, count: counts.disabled },
    { id: 'retired', label: 'Retired', filters: { state: 'retired' }, count: counts.retired },
  ];
}

/** The fields a search term is matched against. */
function searchFields(row: ProviderSummary): ReadonlyArray<string | null | undefined> {
  return [row.name, row.baseUrl, row.modelId];
}

export function ProviderListSection(): ReactNode {
  const navigate = useNavigate();
  const read = useCallback(() => aiApi.listPlatformProviders(), []);

  const [busyId, setBusyId] = useState<string | null>(null);
  const [actionError, setActionError] = useState<Error | null>(null);
  const [testResults, setTestResults] = useState<Record<string, ProviderTestResult>>({});
  const [reload, setReload] = useState<(() => void) | null>(null);

  const actions = providerActionHandlers({
    setBusyId,
    setActionError,
    setTestResults,
    reload: reload ?? (() => undefined),
  });

  const columns = buildColumns({
    busyId,
    testResults,
    onOpen: (provider) => {
      void navigate({
        to: '/platform/ai/providers/$providerId',
        params: { providerId: provider.id },
      });
    },
    onToggleState: actions.toggleState,
    onTest: actions.test,
    onRotate: actions.rotate,
  });

  return (
    <div className="space-y-4">
      {actionError ? <ErrorState title="Action failed" message={actionError.message} /> : null}

      <AppList<ProviderSummary>
        title="AI providers"
        description="The model providers this deployment may call, and the credential each one uses."
        read={read}
        searchFields={searchFields}
        countKeyOf={(row) => row.state}
        buildViews={buildProviderViews}
        columns={columns}
        getRowId={(row) => row.id}
        // Six columns run off a phone screen, so the page asks for the
        // responsive presentation: cards below `sm`, the table above.
        responsive
        onReloadReady={setReload}
        toolbarActions={
          <Button
            size="sm"
            aria-label="Add provider"
            onClick={() => {
              void navigate({ to: '/platform/ai/providers/new' });
            }}
          >
            <FiPlus size={14} aria-hidden="true" />
            <span className="hidden sm:inline">Add provider</span>
          </Button>
        }
        emptyTitle="No providers yet"
        emptyDescription="This deployment has no AI provider. Nothing is broken — AI is optional, and every manual form still works without one."
        noMatchDescription="No provider matches this tab, search or filter."
      />
    </div>
  );
}

type ColumnContext = {
  busyId: string | null;
  testResults: Record<string, ProviderTestResult>;
  onOpen: (provider: ProviderSummary) => void;
  onToggleState: (provider: ProviderSummary) => Promise<void>;
  onTest: (provider: ProviderSummary) => Promise<void>;
  onRotate: (provider: ProviderSummary, apiKey: string) => Promise<void>;
};

function buildColumns(context: ColumnContext): ReadonlyArray<ListColumn<ProviderSummary>> {
  return [
    {
      id: 'name',
      header: 'Provider',
      primary: true,
      sortable: true,
      cell: (row) => (
        <div className="flex flex-col">
          <span className="font-medium text-content">{row.name}</span>
          <span className="text-xs text-content-muted">{row.baseUrl}</span>
          <span className="text-xs text-content-muted">{row.modelId}</span>
        </div>
      ),
    },
    {
      id: 'state',
      header: 'State',
      sortable: true,
      filter: { type: 'select', options: STATE_OPTIONS, placeholder: 'Any' },
      cell: (row) => <Badge tone={providerStateTone(row.state)}>{row.state}</Badge>,
    },
    {
      id: 'capabilities',
      header: 'Capabilities',
      cell: (row) => <CapabilityBadges capabilities={row.capabilities} />,
    },
    {
      id: 'credential',
      header: 'Credential',
      cell: (row) => <CredentialSummaryLine provider={row} />,
    },
    {
      id: 'lastTest',
      header: 'Last test',
      cell: (row) => <LastTestCell provider={row} result={context.testResults[row.id]} />,
    },
    {
      id: 'actions',
      header: '',
      hideable: false,
      cell: (row) => (
        <ProviderRowActions
          provider={row}
          busy={context.busyId === row.id}
          onOpen={context.onOpen}
          onToggleState={context.onToggleState}
          onTest={context.onTest}
          onRotate={context.onRotate}
        />
      ),
    },
  ];
}

type ProviderRowActionsProps = {
  provider: ProviderSummary;
  busy: boolean;
  onOpen: (provider: ProviderSummary) => void;
  onToggleState: (provider: ProviderSummary) => Promise<void>;
  onTest: (provider: ProviderSummary) => Promise<void>;
  onRotate: (provider: ProviderSummary, apiKey: string) => Promise<void>;
};

function ProviderRowActions(props: ProviderRowActionsProps): ReactNode {
  const { provider } = props;
  return (
    <div className="flex flex-wrap gap-2">
      <Button variant="secondary" size="sm" onClick={() => props.onOpen(provider)}>
        Edit
      </Button>
      <Button
        variant="secondary"
        size="sm"
        loading={props.busy}
        onClick={() => {
          void props.onToggleState(provider);
        }}
      >
        {provider.state === 'active' ? 'Disable' : 'Enable'}
      </Button>
      <Button
        variant="secondary"
        size="sm"
        loading={props.busy}
        onClick={() => {
          void props.onTest(provider);
        }}
      >
        Test
      </Button>
      <RotateCredentialDialog
        providerName={provider.name}
        isSubmitting={props.busy}
        error={null}
        onSubmit={(apiKey) => {
          void props.onRotate(provider, apiKey);
        }}
      />
    </div>
  );
}

function LastTestCell({
  provider,
  result,
}: {
  provider: ProviderSummary;
  result: ProviderTestResult | undefined;
}): ReactNode {
  const state = result?.state ?? provider.lastTestState;

  if (state === null || state === undefined) {
    return <span className="text-xs text-content-muted">Not tested yet</span>;
  }

  const detail = result?.detail ?? provider.lastTestDetail;
  const testedAt = result === undefined ? provider.lastTestedAt : null;

  return (
    <div className="flex flex-col">
      <Badge tone={state === 'ok' ? 'positive' : 'critical'}>
        {state === 'ok' ? 'Passed' : 'Failed'}
      </Badge>
      {testedAt ? (
        <span className="mt-1 text-xs text-content-muted">{formatDate(testedAt)}</span>
      ) : null}
      {detail ? <span className="text-xs text-content-muted">{detail}</span> : null}
    </div>
  );
}
